import React, {useState, useRef} from 'react';
import {Box, Flex, Grid} from 'bigdatr-style/layout';
import {styled, useStateWithDeps} from 'bigdatr-style';
import {
    useFloating,
    useFocus,
    useInteractions,
    useClick,
    useRole,
    useListNavigation,
    autoUpdate,
    offset,
    shift,
    flip,
    useDismiss
} from '@floating-ui/react';
import FilterHeading from './FilterHeading';
import {ChevronSmallDown} from 'react-swm-icon-pack';

export type CreativeListOrderBy = {
    field: 'lastAppeared' | 'score' | 'firstAppeared';
    order: 'DESC' | 'ASC';
};

export type ComplianceListOrderBy = {
    field: 'lastAppeared' | 'firstAppeared' | 'complianceRank';
    order: 'DESC' | 'ASC';
};

export const defaultCreativeListOrderBy: CreativeListOrderBy = {
    field: 'lastAppeared',
    order: 'DESC'
};

export const defaultComplianceOrderBy: ComplianceListOrderBy = {
    field: 'complianceRank',
    order: 'DESC'
};

const creativeListOptions: CreativeListOrderBy[] = [
    {field: 'lastAppeared', order: 'DESC'},
    {field: 'firstAppeared', order: 'ASC'},
    {field: 'score', order: 'DESC'}
];

const complianceListOptions: ComplianceListOrderBy[] = [
    {field: 'complianceRank', order: 'DESC'},
    {field: 'complianceRank', order: 'ASC'},
    {field: 'lastAppeared', order: 'DESC'},
    {field: 'firstAppeared', order: 'ASC'}
];

export default function CreativeSortBy(props: {
    value: CreativeListOrderBy | ComplianceListOrderBy;
    onChange: (next: CreativeListOrderBy | ComplianceListOrderBy) => void;
    closeOtherLists?: () => void;
    smallControl?: boolean;
    isOnCompliancePage?: boolean;
}) {
    const {value, smallControl, isOnCompliancePage} = props;

    const options: Array<CreativeListOrderBy | ComplianceListOrderBy> = isOnCompliancePage
        ? complianceListOptions
        : creativeListOptions;

    const selectedItemIndex = options.findIndex(
        (option) => option.field === value.field && option.order === value.order
    );

    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [statefulSelectedIndex, setStatefulSelectedIndex] = useStateWithDeps<number | null>(
        selectedItemIndex > -1 ? selectedItemIndex : null,
        [JSON.stringify(value)]
    );
    const listRef = useRef<HTMLDivElement[]>([]);

    const {refs, floatingStyles, context, middlewareData} = useFloating({
        placement: smallControl ? 'bottom-end' : 'bottom-start',
        open: isOpen,
        onOpenChange: (next) => {
            setIsOpen(next);
            props.closeOtherLists?.();
        },
        whileElementsMounted: autoUpdate,
        // order of middleware functions is important
        middleware: [
            offset(8),
            // keep floater in view if a horizontal scroll would make it offscreen
            shift(),
            // change floater placement to top of reference node if it would be offscreen when scrolling
            flip()
        ]
    });

    const focus = useFocus(context);
    const click = useClick(context);
    const role = useRole(context, {role: 'select'});
    const dismiss = useDismiss(context);

    const listNavigation = useListNavigation(context, {
        listRef,
        activeIndex,
        selectedIndex: statefulSelectedIndex,
        onNavigate: setActiveIndex,
        loop: true
    });

    const {getFloatingProps, getReferenceProps, getItemProps} = useInteractions([
        focus,
        click,
        role,
        listNavigation,
        dismiss
    ]);

    const renderControl = () => {
        if (smallControl) {
            return (
                <Grid
                    data-testid="creative-sort"
                    ref={refs.setReference}
                    {...getReferenceProps()}
                    gridTemplateColumns="1rem auto 1rem"
                    alignItems="center"
                    gap={2}
                    cursor="pointer"
                >
                    <SortIcon />
                    Sort
                    <ChevronSmallDown />
                </Grid>
            );
        }

        return (
            <Control data-testid="creative-sort" ref={refs.setReference} {...getReferenceProps()}>
                <Flex gap={2} alignItems="center">
                    <SortIcon />
                    {selectedItemIndex > -1 ? label(options[selectedItemIndex]) : 'Sort'}
                </Flex>
                <ChevronSmallDown />
            </Control>
        );
    };

    return (
        <Box {...(smallControl ? {} : {pt: 2, px: 3})} width="100%">
            {!smallControl && <FilterHeading title="Sort" />}

            {renderControl()}
            {isOpen && (
                <List
                    ref={refs.setFloating}
                    style={{
                        ...floatingStyles,
                        zIndex: 1,
                        visibility: middlewareData.hide?.referenceHidden ? 'hidden' : 'visible'
                    }}
                    {...getFloatingProps()}
                >
                    {options.map((option, index) => (
                        <ListItem
                            active={activeIndex === index}
                            currentlySelected={statefulSelectedIndex === index}
                            key={`${option.field}-${option.order}`}
                            tabIndex={activeIndex === index ? 0 : -1}
                            ref={(node: HTMLDivElement) => {
                                listRef.current[index] = node;
                            }}
                            {...getItemProps({
                                onClick: () => {
                                    props.onChange(option);
                                    setStatefulSelectedIndex(index);
                                    setIsOpen(false);
                                    props.closeOtherLists?.();
                                }
                            })}
                        >
                            {label(option)}
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
}

function label(option: CreativeListOrderBy | ComplianceListOrderBy) {
    switch (option.field) {
        case 'complianceRank':
            if (option.order === 'DESC') return 'Highest risk first';
            return 'Lowest risk first';
        case 'firstAppeared':
            return 'Oldest first';
        case 'lastAppeared':
            return 'Newest first';
        case 'score':
            return 'Relevance';
        default:
            break;
    }
}

const Control = styled.div`
    background-color: ${(p) => p.theme.colors.background};
    border-radius: 0.5rem;
    padding: 0.5rem 0.5rem 0.5rem 1rem;
    border: 1px solid ${(p) => p.theme.colors.buttonOutline};
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.25rem;
    cursor: pointer;
`;

const List = styled.div`
    border-radius: 0.5rem;
    border: 1px solid ${(p) => p.theme.colors.buttonOutline};
    background-color: ${(p) => p.theme.colors.background};
    overflow: hidden;
`;

const ListItem = styled.div<{active: boolean; currentlySelected: boolean}>`
    padding: 0.5rem 0.75rem;
    width: 100%;
    cursor: pointer;
    ${(p) => (p.currentlySelected ? `background-color: ${p.theme.colors.outlineAlt};` : ``)}
    ${(p) => (p.active ? `background-color: ${p.theme.colors.active};` : ``)}
`;

function SortIcon() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            width="1rem"
            height="1rem"
            viewBox="0 0 18 20"
        >
            <path
                stroke="#180B25"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M13 18.43V5M9 14.43l4 4 4-4M5 1v13.43M9 5 5 1 1 5"
            />
        </svg>
    );
}
